import { useMemo } from 'react';
import { usePipeline } from '../../context/PipelineContext';
import { LEAD_SOURCES, calculateAnnualizedValue } from '../../utils/pipelineCalculations';
import { formatCurrency, formatPercent } from '../../utils/formatting';
import SectionLabel from '../common/SectionLabel';

// Distinct dot colors for each source
const SOURCE_COLORS = {
  referral: '#022889',
  solicitor: '#006D78',
  website: '#2596BE',
  youtube: '#FF6E83',
  podcast: '#3EAAD0',
  social: '#FF9B99',
  event: '#c59000',
  coi: '#6B4C9A',
  other: '#888888',
};

export default function SourcePerformanceDashboard() {
  const { sourceStats, prospects } = usePipeline();

  const tableData = useMemo(() => {
    if (!prospects || prospects.length === 0) return [];

    const rows = [];

    for (const source of LEAD_SOURCES) {
      const sourceProspects = prospects.filter(p => (p.source || 'other') === source.id);
      if (sourceProspects.length === 0) continue;

      const active = sourceProspects.filter(
        p => p.stage !== 'closed-won' && p.stage !== 'closed-lost' && p.stage !== 'unqualified'
      );
      const won = sourceProspects.filter(p => p.stage === 'closed-won');
      const lost = sourceProspects.filter(p => p.stage === 'closed-lost');
      const unqual = sourceProspects.filter(p => p.stage === 'unqualified');

      const totalWonRevenue = won.reduce(
        (sum, p) => sum + calculateAnnualizedValue(p.estimatedValue), 0
      );

      const allValues = sourceProspects.map(p => calculateAnnualizedValue(p.estimatedValue));
      const avgDealValue = allValues.length > 0
        ? allValues.reduce((s, v) => s + v, 0) / allValues.length
        : 0;

      // Source stats now expose qualificationRate alongside winRate.
      const stats = sourceStats?.bySource?.[source.id];
      const winRate = stats?.winRate ?? source.defaultWeight;
      const qualificationRate = stats?.qualificationRate ?? null;
      const isObserved = stats?.isObserved ?? false;

      rows.push({
        id: source.id,
        label: source.label,
        total: sourceProspects.length,
        active: active.length,
        won: won.length,
        lost: lost.length,
        unqual: unqual.length,
        // Backward-compat for the column that says "Lost / Unqual."
        lostUnqual: lost.length + unqual.length,
        winRate,
        qualificationRate,
        isObserved,
        avgDealValue,
        totalWonRevenue,
        conversionWeight: winRate,
        defaultWeight: source.defaultWeight,
      });
    }

    // Sort by total won revenue descending
    rows.sort((a, b) => b.totalWonRevenue - a.totalWonRevenue);
    return rows;
  }, [prospects, sourceStats]);

  const hasAnyObserved = tableData.some(r => r.isObserved);

  if (tableData.length === 0) {
    return (
      <div>
        <SectionLabel>Source Performance</SectionLabel>
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 text-[13px] text-[#888]">
          No prospect data available.
        </div>
      </div>
    );
  }

  return (
    <div>
      <SectionLabel>Source Performance</SectionLabel>

      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-[#e0e0e0]">
                <th className="text-left py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider">Source</th>
                <th className="text-right py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider">Total</th>
                <th className="text-right py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider">Active</th>
                <th className="text-right py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider">Won</th>
                <th className="text-right py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider">Lost</th>
                <th className="text-right py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider">Unqual</th>
                <th className="text-right py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider" title="% of decided prospects that qualified">Qual Rate</th>
                <th className="text-right py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider" title="Won / (Won + Lost) — qualified deals only">Win Rate</th>
                <th className="text-right py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider">Avg Deal Value</th>
                <th className="text-right py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider">Total Won Rev</th>
                <th className="text-right py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider">Conv. Weight</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map(row => (
                <tr
                  key={row.id}
                  className={`border-b border-[#f0f0f0] hover:bg-bone/40 ${
                    row.isObserved ? 'bg-deep-teal/[0.03]' : ''
                  }`}
                >
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: SOURCE_COLORS[row.id] || '#888' }}
                      />
                      <span className="font-bold text-navy">{row.label}</span>
                    </div>
                  </td>
                  <td className="py-2 px-2 text-right">{row.total}</td>
                  <td className="py-2 px-2 text-right">{row.active}</td>
                  <td className="py-2 px-2 text-right text-deep-teal font-bold">{row.won}</td>
                  <td className="py-2 px-2 text-right text-rose">{row.lost}</td>
                  <td className="py-2 px-2 text-right text-[#888]">{row.unqual}</td>
                  <td className="py-2 px-2 text-right">
                    {row.qualificationRate != null ? formatPercent(row.qualificationRate) : <span className="text-[#ccc]">--</span>}
                  </td>
                  <td className="py-2 px-2 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <span>{formatPercent(row.winRate)}</span>
                      {row.isObserved ? (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-[3px] bg-deep-teal/10 text-deep-teal">
                          Observed
                        </span>
                      ) : (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-[3px] bg-[#e8e8e8] text-[#888]">
                          Default
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-2 text-right">{formatCurrency(row.avgDealValue)}</td>
                  <td className="py-2 px-2 text-right font-bold">{formatCurrency(row.totalWonRevenue)}</td>
                  <td className="py-2 px-2 text-right">{formatPercent(row.conversionWeight)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-4 text-[11px] text-[#888] leading-relaxed">
          Conversion weights are used for pipeline weighting. Once 5+ outcomes are recorded per source,
          observed win rates replace defaults.
        </p>
      </div>
    </div>
  );
}
